import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Grid,
  Typography,
  useTheme,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GroupsRoundedIcon from '@mui/icons-material/Groups';
import EventRoundedIcon from '@mui/icons-material/Event';
import ComputerRoundedIcon from '@mui/icons-material/Computer';
import LayersRoundedIcon from '@mui/icons-material/Layers';
import SchoolRoundedIcon from '@mui/icons-material/School';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import toast from 'react-hot-toast';
import moment from 'moment';
import { sessionApi } from '../../../api/sessionApi';
import usePermissions from '../../../hooks/usePermissions';
import useAuth from '../../../hooks/useAuth';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, lightVeil, darkVeil, greyAlpha, grey } from '../../../theme/tones';

// ── Status Configuration ──
// Seuil d'inscrits en dessous duquel une session est considérée « À risque »
const AT_RISK_THRESHOLD = 5;

export const KANBAN_STATUSES = [
  { id: 'À risque', label: 'À risque', color: getTone('amber').base, bg: toneAlpha('amber', 0.08), border: toneAlpha('amber', 0.25) },
  { id: 'Confirmée', label: 'Confirmée', color: getTone('blue').color, bg: toneAlpha('blue', 0.08), border: toneAlpha('blue', 0.2) },
  { id: 'En cours', label: 'En cours', color: getTone('emerald').color, bg: toneAlpha('emerald', 0.08), border: toneAlpha('emerald', 0.2) },
  { id: 'Terminée', label: 'Terminée', color: getTone('teal').color, bg: toneAlpha('teal', 0.08), border: toneAlpha('teal', 0.2) },
  { id: 'Annulée', label: 'Annulée', color: getTone('rose').color, bg: toneAlpha('rose', 0.08), border: toneAlpha('rose', 0.2) }
];

const AVATAR_GRADIENTS = [
  [getTone('blue').base, getTone('blue').color],
  [getTone('violet').base, getTone('violet').color],
  [getTone('cyan').base, getTone('cyan').deep],
  [getTone('emerald').base, getTone('emerald').color],
  [getTone('amber').base, getTone('amber').color],
  [getTone('pink').base, getTone('pink').deep],
  [getTone('indigo').base, getTone('indigo').color],
  [getTone('teal').base, getTone('teal').color],
];

const hashString = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SE';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getFormatStyle = (val = '') => {
  // Formats issus de « Mixte » : testés en premier pour conserver sa couleur
  // (« Présentiel PI » contient « Présentiel » et serait sinon capté au-dessus).
  if (val === 'Présentiel PI' || val === 'Classe virtuelle PI') {
    return { color: getTone('teal').color, bg: toneAlpha('teal', 0.09), border: toneAlpha('teal', 0.22), icon: <LayersRoundedIcon sx={{ fontSize: '0.9rem' }} /> };
  }
  if (val.includes('E-Learning') || val.includes('E-learning')) {
    return { color: getTone('indigo').base, bg: toneAlpha('indigo', 0.09), border: toneAlpha('indigo', 0.22), icon: <ComputerRoundedIcon sx={{ fontSize: '0.85rem' }} /> };
  }
  if (val.includes('Présentiel')) {
    return { color: getTone('violet').base, bg: toneAlpha('violet', 0.09), border: toneAlpha('violet', 0.22), icon: <GroupsRoundedIcon sx={{ fontSize: '0.85rem' }} /> };
  }
  if (val.includes('Mixte') || val.includes('virtuelle')) {
    return { color: getTone('teal').color, bg: toneAlpha('teal', 0.09), border: toneAlpha('teal', 0.22), icon: <LayersRoundedIcon sx={{ fontSize: '0.85rem' }} /> };
  }
  return { color: getTone('neutral').base, bg: toneAlpha('neutral', 0.07), border: toneAlpha('neutral', 0.16), icon: <SchoolRoundedIcon sx={{ fontSize: '0.85rem' }} /> };
};

// ── Smart Session Column Classification ──
export const getSessionKanbanStatus = (session) => {
  const status = session.status || 'Active';

  // Une session annulée garde toujours sa colonne, elle n'est jamais « À risque ».
  if (status === 'Annulée') return 'Annulée';

  // « À risque » : toute session (hors Annulée) comptant moins de 5 inscrits.
  const count = parseInt(session.nombre, 10) || 0;
  if (count < AT_RISK_THRESHOLD) return 'À risque';

  // À partir d'ici la session a au moins 5 inscrits : classement normal.
  if (status === 'Confirmée') return 'Confirmée';
  if (status === 'En cours') return 'En cours';
  if (status === 'Terminée') return 'Terminée';

  // Statuts legacy/Active/Offerte/Brouillon : on calcule selon les dates.
  const today = moment().startOf('day');
  const startDate = moment(session.start_date, 'DD/MM/YYYY', true);
  const endDate = moment(session.end_date, 'DD/MM/YYYY', true);

  if (endDate.isValid() && endDate.isBefore(today, 'day')) {
    return 'Terminée';
  }
  if (
    startDate.isValid() &&
    startDate.isSameOrBefore(today, 'day') &&
    endDate.isValid() &&
    endDate.isSameOrAfter(today, 'day')
  ) {
    return 'En cours';
  }

  // À venir avec au moins 5 inscrits => Confirmée.
  return 'Confirmée';
};

const getRemainingDaysLabel = (startDateStr) => {
  const startDate = moment(startDateStr, 'DD/MM/YYYY', true);
  if (!startDate.isValid()) return '';
  const today = moment().startOf('day');
  const diff = startDate.diff(today, 'days');
  if (diff === 0) return 'Aujourd\'hui';
  if (diff > 0) return `J-${diff}`;
  return `J+${Math.abs(diff)}`;
};

const SessionFormationsKanban = (props) => {
  const { filteredSessions, setSessionsFormation } = props;
  const theme = useTheme();
  // Déplacer une carte change le statut de la session : même droit que dans la
  // fiche session (sessions.update_status). Aligné le 2026-09-21 : le Kanban
  // était jusque-là ouvert à tous, la fiche réservée aux administrateurs.
  const { user } = useAuth();
  const { can } = usePermissions();
  const canMove = can('sessions.update_status', user.access_level === 0);
  const isLight = theme.palette.mode === 'light';

  // Group sessions by their Kanban status
  const groupedSessions = KANBAN_STATUSES.reduce((acc, status) => {
    acc[status.id] = [];
    return acc;
  }, {});

  filteredSessions.forEach((session) => {
    const colId = getSessionKanbanStatus(session);
    if (groupedSessions[colId]) {
      groupedSessions[colId].push(session);
    } else {
      // Default fallback
      groupedSessions['À risque'].push(session);
    }
  });

  // ── Drag and Drop Handlers ──
  const handleDragStart = (e, sessionId) => {
    e.dataTransfer.setData('text/plain', sessionId.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    const sessionId = e.dataTransfer.getData('text/plain');
    if (!sessionId) return;

    // « À risque » est une colonne calculée (< 5 inscrits) : on ne peut pas y
    // déposer manuellement une session, son appartenance dépend des inscriptions.
    if (targetStatus === 'À risque') {
      toast('« À risque » est automatique : il dépend du nombre d\'inscrits.', { icon: 'ℹ️' });
      return;
    }

    // Find the session to verify its status changes
    // If it's already in targetStatus, do nothing
    const sessionToUpdate = filteredSessions.find((s) => s.id.toString() === sessionId);
    if (!sessionToUpdate || getSessionKanbanStatus(sessionToUpdate) === targetStatus) return;

    // Update local state instantly for extreme UI responsiveness
    setSessionsFormation((prevSessions) =>
      prevSessions.map((s) => {
        if (s.id.toString() === sessionId) {
          return { ...s, status: targetStatus };
        }
        return s;
      })
    );

    // Persist via Backend API
    try {
      await sessionApi.editSession('status', targetStatus, sessionId, localStorage.getItem('accessToken'));
      toast.success(`Session déplacée vers "${targetStatus}"`);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  return (
    <Box sx={{ overflowX: 'auto', pb: 3, pt: 1 }}>
      <Grid container spacing={2.5} sx={{ minWidth: 1200, flexWrap: 'nowrap' }}>
        {KANBAN_STATUSES.map((status) => {
          const sessions = groupedSessions[status.id] || [];
          return (
            <Grid
              item
              key={status.id}
              sx={{
                width: '20%',
                flexShrink: 0,
                minWidth: 260
              }}
            >
              {/* Column Container */}
              <Box
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status.id)}
                sx={{
                  borderRadius: '20px',
                  p: 2,
                  height: '100%',
                  minHeight: 500,
                  backgroundColor: isLight ? greyAlpha(100, 0.7) : greyAlpha(800, 0.3),
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: isLight ? greyAlpha(100, 0.9) : greyAlpha(800, 0.4)
                  }
                }}
              >
                {/* Column Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: status.color,
                        boxShadow: `0 0 6px ${status.color}`
                      }}
                    />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: 'text.primary' }}>
                      {status.label}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: isLight ? grey(0) : greyAlpha(900, 0.6),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {sessions.length}
                  </Box>
                </Box>

                {/* Cards Container */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {sessions.map((session) => {
                    const grad = AVATAR_GRADIENTS[hashString(session.name) % AVATAR_GRADIENTS.length];
                    const formatStyle = getFormatStyle(session.format || '');
                    const daysLabel = getRemainingDaysLabel(session.start_date);
                    
                    return (
                      <Card
                        key={session.id}
                        draggable={canMove}
                        onDragStart={(e) => (canMove ? handleDragStart(e, session.id) : e.preventDefault())}
                        sx={{
                          p: 2,
                          borderRadius: '16px',
                          border: '1px solid',
              borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.text.primary, 0.02)}`,
                          cursor: 'grab',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: isLight ? `0 12px 24px ${greyAlpha(900, 0.06)}` : `0 12px 24px ${darkVeil(0.25)}`,
                            borderColor: 'secondary.main'
                          },
                          '&:active': {
                            cursor: 'grabbing'
                          }
                        }}
                      >
                        {/* Header: Avatar + Title & Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                          <Avatar
                            src={session.image || undefined}
                            sx={{
                              height: 38,
                              width: 38,
                              borderRadius: '11px',
                              fontSize: '0.8rem',
                              fontWeight: 800,
                              color: grey(0),
                              background: `linear-gradient(135deg, ${grad[0]} 0%, ${grad[1]} 100%)`,
                              boxShadow: `0 4px 10px ${grad[0]}30`,
                              flexShrink: 0
                            }}
                          >
                            {getInitials(session.name)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Tooltip
                              title={session.name || ''}
                              arrow
                              componentsProps={{ tooltip: { sx: { fontSize: '0.74rem', borderRadius: '8px' } } }}
                            >
                              <Typography
                                component={RouterLink}
                                to={`/dashboard/session/${session.id}`}
                                sx={{
                                  textDecoration: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  color: 'text.primary',
                                  lineHeight: 1.3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  '&:hover': { color: 'secondary.main' }
                                }}
                              >
                                {session.name}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Tooltip title="Détails" arrow>
                            <IconButton
                              component={RouterLink}
                              to={`/dashboard/session/${session.id}`}
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                mt: -0.5,
                                mr: -0.5,
                                transition: 'all 0.15s',
                                '&:hover': { color: 'secondary.main' }
                              }}
                            >
                              <NorthEastRoundedIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {/* Format Badges */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                          <Chip
                            icon={formatStyle.icon}
                            label={session.format || 'Format inconnu'}
                            sx={{
                              height: 22,
                              fontSize: '0.64rem',
                              fontWeight: 700,
                              backgroundColor: formatStyle.bg,
                              color: formatStyle.color,
                              border: '1px solid',
                              borderColor: formatStyle.border,
                              borderRadius: '7px',
                              '& .MuiChip-icon': { color: 'inherit' }
                            }}
                          />
                          {session.program_number && session.program_number !== 'N/A' && (
                            <Chip
                              label={`N° Prog. ${session.program_number.split(',')[0].trim()}`}
                              sx={{
                                height: 22,
                                fontSize: '0.64rem',
                                fontWeight: 700,
                                fontFamily: '"Roboto Mono", monospace',
                                backgroundColor: isLight ? toneAlpha('neutral', 0.06) : lightVeil(0.05),
                                color: 'text.secondary',
                                border: '1px solid',
                                borderColor: isLight ? toneAlpha('neutral', 0.15) : lightVeil(0.08),
                                borderRadius: '7px'
                              }}
                            />
                          )}
                        </Box>

                        {/* Divider */}
                        <Divider sx={{ my: 1, opacity: 0.5 }} />

                        {/* Footer: Date / remaining days + learners ratio */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                          {/* Date and Countdown */}
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 600 }}>
                              Début : {session.start_date}
                            </Typography>
                            {daysLabel && (
                              <Typography sx={{ fontSize: '0.62rem', color: 'secondary.main', fontWeight: 800, mt: 0.2 }}>
                                {daysLabel}
                              </Typography>
                            )}
                          </Box>

                          {/* Conventions signées / Apprenants inscrits (Inscrit + InscritD) */}
                          <Tooltip
                            title={`${session.conventions_signees || 0} convention(s) signée(s) / ${session.nombre_inscrits || 0} apprenant(s) inscrit(s) (Inscrit + InscritD)`}
                            arrow
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.8,
                                py: 0.3,
                                borderRadius: '8px',
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                                border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                color: 'success.main',
                                fontSize: '0.66rem',
                                fontWeight: 800
                              }}
                            >
                              <GroupsRoundedIcon sx={{ fontSize: '0.8rem' }} />
                              {session.conventions_signees || 0} / {session.nombre_inscrits || 0}
                            </Box>
                          </Tooltip>
                        </Box>
                      </Card>
                    );
                  })}

                  {sessions.length === 0 && (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        border: '1px dashed',
                        borderColor: isLight ? toneAlpha('neutral', 0.3) : lightVeil(0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.disabled',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}
                    >
                      Aucune session
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

SessionFormationsKanban.propTypes = {
  filteredSessions: PropTypes.array.isRequired,
  setSessionsFormation: PropTypes.func.isRequired
};

export default SessionFormationsKanban;
